import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CollectionFile } from "shared/types/files";
import { openCreatePostModal } from "features/post";
import { convertCollectionFileToPostMediaFile } from "features/media-library";
import { getModalsLoading } from "selectors/commonSelectors";
import { getSelectedAccounts } from "selectors/skedCoreSelectors";
import {
  hideModal,
  showConfirmModal,
  showBulkMoveAssets,
  showDuplicateAssets,
  showEditAssetModal,
} from "actions/modal";
import {
  toggleCollectionAsset,
  updateCollectionAsset,
  filterSelectedAssets,
  deleteCollectionAssets,
} from "actions/mediaLibrary";
import {
  getSelectedAssetIds,
  getSelectedCollection,
} from "selectors/mediaLibrarySelectors";
import { Alert, AlertType } from "ui/alert";
import { useTrackEvent } from "libs/analytics";
import { BUTTON_TYPES } from "constants/SkedButton";
import {
  CREATE_POST_FROM_LIBRARY_SCREEN,
  SELECTED_MEDIA_FROM_LIBRARY,
} from "constants/Events";
import { isManagedLibraryCollection } from "components/MediaLibrary/Utils";
import {
  uploadFileToCollection,
  replaceAsset,
  uploadFile,
  FileToUpload,
} from "api/media-library";
import { reloadAssetCurrentPage, reloadTags } from "actions/mediaLibrary";
import type { BulkCreatePostActiveContext } from "../features/post/bulk-create/bulk-create-post.active-context";
import { entitlementFeatureToggle } from "../utils/feature-toggle-utils";
import { openCreatePostModalV2 } from "../features/post/bulk-create/bulk-create-post.actions";
import { clearScene, migrateScene } from "../utils/cesdk-scene-storage";
import { Store } from "../store";
import { convertCustomerUploadsS3ToCloudFront } from "../libs/media-files";

// Helper function to check if asset is used based on media type
const isAssetUsedByType = (asset: CollectionFile): boolean => {
  if (!asset) return false;

  const hasUpcomingPosts = !!(
    asset.usedInUpcomingPosts && asset.usedInUpcomingPosts.length > 0
  );
  const hasDraftPosts = !!(
    asset.usedInDraftPosts && asset.usedInDraftPosts.length > 0
  );

  return hasUpcomingPosts || hasDraftPosts;
};

// Helper functions moved to outer scope
const getSaveButtonText = (isUsed: boolean): string => {
  return isUsed ? "Save a version" : "Save";
};

const getSaveMessage = ({
  assetIsUsed,
  assetDisplayType,
}: {
  assetIsUsed: boolean;
  assetDisplayType: "photo" | "video";
}): string => {
  if (assetDisplayType === "photo") {
    return assetIsUsed
      ? `This image is being used in an upcoming post. You are creating a new version, make a copy in Library if you would like to keep the original or modify the media through edit post.`
      : `You are editing the original image, make a copy in Library if you would like to keep the original.`;
  }

  if (assetDisplayType === "video") {
    return assetIsUsed
      ? `This video is being used in an upcoming post. You are creating a new version, make a copy in Library if you would like to keep the original.`
      : `You are editing the original video, make a copy in Library if you would like to keep the original.`;
  }

  return "";
};

export interface UseGridMediaCardReturn {
  // Asset properties
  id: string;
  selected: boolean;
  selecting: boolean;
  assetUrl: string;
  gifPreviewUrl?: string;
  lowQualityVideoUrl?: string;
  favorited: boolean;
  isVideo: boolean;
  canSelect: boolean;
  canFavorite: boolean;
  isManagedCollection: boolean;
  assetIsUsed: boolean;
  isEditable: boolean;

  // Photo editor properties
  showPhotoEditor: boolean;
  photoEditorImageUrl: string;
  photoEditorAssetId: string;
  photoEditorAssetUrl: string;
  photoEditorIsReplacingOriginal: boolean;
  photoEditorProgress: number | null;
  photoEditorSaveButtonText: string;
  photoEditorMessage: string;
  photoEditorIsSaving: boolean;

  // Video editor properties
  showVideoEditor: boolean;
  videoEditorVideoUrl: string;
  videoEditorAssetId: string;
  videoEditorAssetUrl: string;
  videoEditorIsReplacingOriginal: boolean;
  videoEditorProgress: number | null;
  videoEditorSaveButtonText: string;
  videoEditorMessage: string;
  videoEditorIsSaving: boolean;

  // Event handlers
  handleCardClick: (e?: React.MouseEvent<HTMLDivElement>) => void;
  handleFavoriteClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleCreatePost: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleCopy: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  handleMove: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  handleDelete: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  handleViewDetails: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  handleEdit: (e?: React.MouseEvent<HTMLButtonElement>) => void;

  handlePhotoEditorClose: () => void;
  handlePhotoEditorSave: (blob: Blob) => Promise<boolean>;
  handleVideoEditorClose: () => void;
  handleVideoEditorSave: (blob: Blob) => Promise<boolean>;
}

export const useGridMediaCard = (
  asset: CollectionFile,
  onCardClick?: (e: React.MouseEvent<HTMLDivElement>) => void,
  options?: {
    canSelect?: boolean;
    canFavorite?: boolean;
  }
): UseGridMediaCardReturn => {
  const dispatch = useDispatch();
  const selectedAssetIds = useSelector(getSelectedAssetIds);
  const modalLoading = useSelector(getModalsLoading);
  const selectedAccounts = useSelector(getSelectedAccounts);
  const selectedCollection = useSelector(getSelectedCollection);
  const { isManagedCollection } =
    isManagedLibraryCollection(selectedCollection);
  const trackEvent = useTrackEvent();

  // Extract control options with defaults
  const canSelect = options?.canSelect ?? true;
  const canFavorite = options?.canFavorite ?? true;

  // Photo editor state
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [photoEditorProgress, setPhotoEditorProgress] = useState<number | null>(
    null
  );
  const [photoEditorIsSaving, setPhotoEditorIsSaving] = useState(false);

  // Video editor state
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [videoEditorProgress, setVideoEditorProgress] = useState<number | null>(
    null
  );
  const [videoEditorIsSaving, setVideoEditorIsSaving] = useState(false);

  // Extract used flag logic to variables for cleaner code
  const assetIsUsed = isAssetUsedByType(asset);

  // Get save button text and message based on asset usage
  const photoEditorSaveButtonText = getSaveButtonText(assetIsUsed);
  const photoEditorMessage = getSaveMessage({
    assetIsUsed,
    assetDisplayType: "photo",
  });
  const videoEditorSaveButtonText = getSaveButtonText(assetIsUsed);
  const videoEditorMessage = getSaveMessage({
    assetDisplayType: "video",
    assetIsUsed,
  });

  // Internal click handlers
  const handleCardClick = (e?: React.MouseEvent<HTMLDivElement>) => {
    if (onCardClick) {
      // Create a synthetic event if one wasn't provided (e.g., from checkbox)
      const syntheticEvent =
        e ||
        ({
          stopPropagation: () => {},
          preventDefault: () => {},
        } as React.MouseEvent<HTMLDivElement>);
      onCardClick(syntheticEvent);
    } else {
      dispatch(toggleCollectionAsset(asset));
      dispatch(filterSelectedAssets());
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    dispatch(
      updateCollectionAsset(
        asset,
        null,
        null,
        null,
        null,
        null,
        null,
        !asset.favorite
      )
    );
  };

  // Action handlers
  const handleCreatePost = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    trackEvent({
      eventName: SELECTED_MEDIA_FROM_LIBRARY,
      metadata: {
        length: 1,
      },
    });
    trackEvent({
      eventName: CREATE_POST_FROM_LIBRARY_SCREEN,
      metadata: {
        length: 1,
      },
    });

    if (entitlementFeatureToggle.get("bulk-create")) {
      dispatch(
        openCreatePostModalV2({
          accounts: selectedAccounts,
          media: {
            type: "provided-media",
            assets: [convertCollectionFileToPostMediaFile(asset)],
          },
          postCloseBehavior: {
            type: "callback",
            callback: (context: BulkCreatePostActiveContext) => {
              if (context.isAnyFormSaved()) {
                Store.dispatch(reloadAssetCurrentPage());
                Store.dispatch(reloadTags());
              }
            },
          },
        })
      );
    } else {
      dispatch(
        openCreatePostModal({
          accounts: selectedAccounts,
          assets: [convertCollectionFileToPostMediaFile(asset)],
        })
      );
    }
  };

  const handleCopy = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    dispatch(showDuplicateAssets([asset._id], selectedCollection, [asset]));
  };

  const handleMove = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    dispatch(showBulkMoveAssets([asset]));
  };

  const handleDelete = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    dispatch(
      showConfirmModal({
        title: "Delete asset",
        messageJsx: (
          <div>
            <p>
              Are you sure you want to delete the asset &quot;
              <strong>{asset.name || "Untitled"}</strong>&quot;?
            </p>
            <Alert type={AlertType.warning}>
              Once deleted, posts with this asset will fail if you haven&apos;t
              updated the asset within posts.
            </Alert>
          </div>
        ),
        confirmType: BUTTON_TYPES.ALERT,
        confirmText: "Delete",
        cancelType: BUTTON_TYPES.PRIMARY_OUTLINE,
        confirmationTextToEnter: "delete",
        handleConfirm: () => dispatch(deleteCollectionAssets([asset])),
      })
    );
  };

  const handleViewDetails = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (modalLoading) return;
    // Replace the single details request; the mounted sheet guards unsaved edits.
    dispatch(hideModal(false, "EDIT_COLLECTION_ASSET"));
    dispatch(showEditAssetModal(asset, false));
  };

  // Photo and video editor handlers
  const handleEdit = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    console.log(
      "handleEdit - Asset type:",
      asset.meta?.isVideo ? "video" : "image"
    );
    console.log("handleEdit - Asset meta:", asset?.meta);

    if (asset?.meta?.isVideo) {
      console.log("handleEdit - Opening video editor");
      setShowVideoEditor(true);
    } else {
      console.log("handleEdit - Opening photo editor");
      setShowPhotoEditor(true);
    }
  };

  const handlePhotoEditorClose = () => {
    setShowPhotoEditor(false);
    setPhotoEditorProgress(null);
    setPhotoEditorIsSaving(false);
  };

  const handleVideoEditorClose = () => {
    setShowVideoEditor(false);
    setVideoEditorProgress(null);
    setVideoEditorIsSaving(false);
  };

  const handlePhotoEditorSave = async (blob: Blob) => {
    console.log("PhotoEditor Save - Starting save operation");
    console.log("Asset type:", asset?.meta?.isVideo ? "video" : "image");
    console.log("Blob type:", blob.type);
    console.log("Blob size:", blob.size);

    setPhotoEditorIsSaving(true);
    setPhotoEditorProgress(0);
    try {
      if (assetIsUsed) {
        console.log("PhotoEditor Save - Save as new version (used asset)");
        // Save as new version - create new asset and keep original
        // Create a FileToUpload object for the blob
        const fileToUpload: FileToUpload = {
          contentType: "blob",
          name: asset.name || "Untitled",
          blob: blob,
          type: asset?.meta?.isVideo ? "video" : "image",
        };

        console.log("FileToUpload object:", fileToUpload);

        // Upload the edited file as a new asset
        const uploadResult = await uploadFile(fileToUpload, {
          onProgress: (progress) => {
            console.log("Upload progress:", progress);
            setPhotoEditorProgress(progress.value);
          },
        });

        console.log("Upload result:", uploadResult);

        // Add the new asset to the collection
        if ("url" in uploadResult) {
          await uploadFileToCollection({
            collectionId: asset.inbox_id,
            file: { url: uploadResult.url, name: asset?.name || "Untitled" },
          });
        }

        setPhotoEditorProgress(100);
        // Close the modal after a brief delay to show completion
        setTimeout(() => {
          handlePhotoEditorClose();
        }, 500);

        // Refresh the library to show the new asset
        dispatch(reloadAssetCurrentPage());
        dispatch(reloadTags());
      } else {
        console.log("PhotoEditor Save - Replace existing asset (unused asset)");
        // Replace existing asset - create new asset and replace original
        // Create a FileToUpload object for the blob
        const fileToUpload: FileToUpload = {
          contentType: "blob",
          name: asset.name || "Untitled",
          blob: blob,
          type: asset?.meta?.isVideo ? "video" : "image",
        };

        console.log("FileToUpload object:", fileToUpload);

        // Upload the edited file as a new asset
        const uploadResult = await uploadFile(fileToUpload, {
          onProgress: (progress) => {
            console.log("Upload progress:", progress);
            setPhotoEditorProgress(progress.value);
          },
        });

        console.log("Upload result:", uploadResult);

        // Add the new asset to the collection
        const newAsset = await ("url" in uploadResult
          ? uploadFileToCollection({
              collectionId: asset.inbox_id,
              file: { url: uploadResult.url, name: asset?.name || "Untitled" },
            })
          : uploadFileToCollection({
              collectionId: asset.inbox_id,
              file: {
                transcodingJobId: uploadResult.transcodingJobId,
                name: asset.name || "Untitled",
              },
            }));

        console.log("New asset created:", newAsset);

        // Then use the replace endpoint to replace the original asset
        await replaceAsset(asset.inbox_id, {
          originalAssetId: asset._id,
          replacementAssetId: newAsset._id,
        });

        console.log("Asset replaced successfully");

        // Migrate scene from old asset ID to new asset ID
        // The scene was saved with the old asset ID in the editor's onSave callback
        // Now we need to move it to the new asset ID
        const newAssetUrl = newAsset.downloadUrl || newAsset.storage?.url;
        console.log("Migrating scene from", asset?._id, "to", newAsset._id);
        await migrateScene(asset._id, newAsset._id, newAssetUrl);

        setPhotoEditorProgress(100);
        // Close the modal after a brief delay to show completion
        setTimeout(() => {
          handlePhotoEditorClose();
        }, 500);

        // Scene is saved automatically in the editor's onSave callback
        // No need to clear scene here since we're replacing the original

        // Refresh the library to show the updated asset
        dispatch(reloadAssetCurrentPage());
        dispatch(reloadTags());
      }
      return true;
    } catch (error) {
      console.error("Error saving edited image:", error);
      setPhotoEditorProgress(null);
      return false;
    } finally {
      setPhotoEditorIsSaving(false);
    }
  };

  const handleVideoEditorSave = async (blob: Blob) => {
    console.log("VideoEditor Save - Starting save operation");
    console.log("Asset type:", asset?.meta?.isVideo ? "video" : "image");
    console.log("Blob type:", blob.type);
    console.log("Blob size:", blob.size);

    setVideoEditorIsSaving(true);
    setVideoEditorProgress(0);
    try {
      if (assetIsUsed) {
        console.log("VideoEditor Save - Save as new version (used asset)");
        // Save as new version - create new asset and keep original
        // Create a FileToUpload object for the blob
        const fileToUpload: FileToUpload = {
          contentType: "blob",
          name: asset.name || "Untitled",
          blob: blob,
          type: asset?.meta?.isVideo ? "video" : "image",
        };

        console.log("FileToUpload object:", fileToUpload);

        // Upload the edited file as a new asset
        const uploadResult = await uploadFile(fileToUpload, {
          onProgress: (progress) => {
            console.log("Upload progress:", progress);
            setVideoEditorProgress(progress.value);
          },
        });

        console.log("Upload result:", uploadResult);

        // Add the new asset to the collection
        await ("url" in uploadResult
          ? uploadFileToCollection({
              collectionId: asset.inbox_id,
              file: { url: uploadResult.url, name: asset?.name || "Untitled" },
            })
          : uploadFileToCollection({
              collectionId: asset.inbox_id,
              file: {
                transcodingJobId: uploadResult.transcodingJobId,
                name: asset.name || "Untitled",
              },
            }));

        setVideoEditorProgress(100);
        // Close the modal after a brief delay to show completion
        setTimeout(() => {
          handleVideoEditorClose();
        }, 500);

        // Clear scene for "save version" flow (creates new asset, doesn't replace)
        await clearScene(asset._id);

        // Refresh the library to show the new asset
        dispatch(reloadAssetCurrentPage());
        dispatch(reloadTags());
      } else {
        console.log("VideoEditor Save - Replace existing asset (unused asset)");
        // Replace existing asset - create new asset and replace original
        // Create a FileToUpload object for the blob
        const fileToUpload: FileToUpload = {
          contentType: "blob",
          name: asset.name || "Untitled",
          blob: blob,
          type: asset?.meta?.isVideo ? "video" : "image",
        };

        console.log("FileToUpload object:", fileToUpload);

        // Upload the edited file as a new asset
        const uploadResult = await uploadFile(fileToUpload, {
          onProgress: (progress) => {
            console.log("Upload progress:", progress);
            setVideoEditorProgress(progress.value);
          },
        });

        console.log("Upload result:", uploadResult);

        // Add the new asset to the collection
        const newAsset = await ("url" in uploadResult
          ? uploadFileToCollection({
              collectionId: asset.inbox_id,
              file: { url: uploadResult.url, name: asset?.name || "Untitled" },
            })
          : uploadFileToCollection({
              collectionId: asset.inbox_id,
              file: {
                transcodingJobId: uploadResult.transcodingJobId,
                name: asset.name || "Untitled",
              },
            }));

        console.log("New asset created:", newAsset);
        console.log("New asset ID:", newAsset._id);
        console.log(
          "New asset URL:",
          newAsset.downloadUrl || newAsset.storage?.url
        );

        // Then use the replace endpoint to replace the original asset
        await replaceAsset(asset.inbox_id, {
          originalAssetId: asset._id,
          replacementAssetId: newAsset._id,
        });

        console.log("Asset replaced successfully");

        // Migrate scene from old asset ID to new asset ID
        // The scene was saved with the old asset ID in the editor's onSave callback
        // Now we need to move it to the new asset ID
        const newAssetUrl = newAsset.downloadUrl || newAsset.storage?.url;
        console.log("Migrating scene from", asset?._id, "to", newAsset._id);
        await migrateScene(asset._id, newAsset._id, newAssetUrl);

        setVideoEditorProgress(100);
        // Close the modal after a brief delay to show completion
        setTimeout(() => {
          handleVideoEditorClose();
        }, 500);

        // Scene is saved automatically in the editor's onSave callback
        // No need to clear scene here since we're replacing the original

        // Refresh the library to show the updated asset
        dispatch(reloadAssetCurrentPage());
        dispatch(reloadTags());
      }
      return true;
    } catch (error) {
      console.error("Error saving edited video:", error);
      setVideoEditorProgress(null);
      return false;
    } finally {
      setVideoEditorIsSaving(false);
    }
  };
  const assetUrl = convertCustomerUploadsS3ToCloudFront(
    asset.thumbnail || asset.downloadUrl || ""
  );
  const normalizedEditorAssetUrl = convertCustomerUploadsS3ToCloudFront(
    asset.downloadUrl || asset.storage?.url || ""
  );
  const gifPreviewUrl = asset.gifThumbnail
    ? convertCustomerUploadsS3ToCloudFront(asset.gifThumbnail)
    : asset.gifThumbnail;
  const lowQualityVideoUrl = asset.lowQualityVideo?.url
    ? convertCustomerUploadsS3ToCloudFront(asset.lowQualityVideo.url)
    : asset.lowQualityVideo?.url;
  return {
    // Asset properties
    id: asset._id,
    selected: selectedAssetIds.includes(asset.asset_id),
    selecting: selectedAssetIds.length > 0,
    assetUrl,
    gifPreviewUrl,
    lowQualityVideoUrl,
    favorited: asset.favorite ?? false,
    isVideo: !!asset.meta?.isVideo,
    canSelect,
    canFavorite,
    isManagedCollection,
    assetIsUsed,
    isEditable: !!(asset.meta?.isVideo || asset.meta?.isImage),
    // Photo editor properties
    showPhotoEditor,
    photoEditorImageUrl: normalizedEditorAssetUrl,
    photoEditorAssetId: asset._id,
    // Keep assetUrl raw to preserve scene identity for previously saved CE SDK scenes.
    photoEditorAssetUrl: asset.downloadUrl || asset.storage?.url || "",
    photoEditorIsReplacingOriginal: !assetIsUsed,
    photoEditorProgress,
    photoEditorSaveButtonText,
    photoEditorMessage,
    photoEditorIsSaving,

    // Video editor properties
    showVideoEditor,
    videoEditorVideoUrl: normalizedEditorAssetUrl,
    videoEditorAssetId: asset._id,
    // Keep assetUrl raw to preserve scene identity for previously saved CE SDK scenes.
    videoEditorAssetUrl: asset.downloadUrl || asset.storage?.url || "",
    videoEditorIsReplacingOriginal: !assetIsUsed,
    videoEditorProgress,
    videoEditorSaveButtonText,
    videoEditorMessage,
    videoEditorIsSaving,

    // Event handlers
    handleCardClick,
    handleFavoriteClick,
    handleCreatePost,
    handleCopy,
    handleMove,
    handleDelete,
    handleViewDetails,
    handleEdit,

    handlePhotoEditorClose,
    handlePhotoEditorSave,
    handleVideoEditorClose,
    handleVideoEditorSave,
  };
};
